import { type Folder, FOLDERS, type Intent, INTENTS, type QueueItemDetail } from '@rp/contracts'
import { useEffect, useState } from 'react'
import { isApiError } from '../../api/client'
import { Button, Dialog, Field } from '../../design'
import { FOLDER_LABELS, INTENT_LABELS } from '../queue/labels'
import { RECLASSIFY_COPY } from './copy'
import { useReclassify } from './useReclassify'

export const ReclassifyDialog = ({
  item,
  open,
  onClose,
}: {
  item: QueueItemDetail
  open: boolean
  onClose: () => void
}) => {
  const [intent, setIntent] = useState<Intent | ''>(item.classification.intent ?? '')
  const [inDomain, setInDomain] = useState(item.classification.inDomain ?? true)
  const [folder, setFolder] = useState<Folder>(item.folder)
  const mutation = useReclassify(item.id)
  const resetMutation = mutation.reset

  useEffect(() => {
    if (!open) return
    setIntent(item.classification.intent ?? '')
    setInDomain(item.classification.inDomain ?? true)
    setFolder(item.folder)
    resetMutation()
  }, [item.classification.inDomain, item.classification.intent, item.folder, resetMutation, open])

  const save = () => {
    if (intent === '') return
    mutation.mutate({ intent, inDomain, folder }, { onSuccess: onClose })
  }

  return (
    <Dialog
      open={open}
      title={RECLASSIFY_COPY.title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{RECLASSIFY_COPY.cancel}</Button>
          <Button
            variant="primary"
            disabled={intent === ''}
            pending={mutation.isPending}
            pendingLabel={RECLASSIFY_COPY.saving}
            onClick={save}
          >
            {RECLASSIFY_COPY.save}
          </Button>
        </>
      }
    >
      <Field id="reclassify-intent" label={RECLASSIFY_COPY.intent}>
        {({ id }) => (
          <select id={id} value={intent} onChange={(event) => setIntent(event.target.value as Intent | '')}>
            <option value="">{RECLASSIFY_COPY.intentPlaceholder}</option>
            {INTENTS.map((value) => (
              <option key={value} value={value}>
                {INTENT_LABELS[value]}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field id="reclassify-folder" label={RECLASSIFY_COPY.folder}>
        {({ id }) => (
          <select id={id} value={folder} onChange={(event) => setFolder(event.target.value as Folder)}>
            {FOLDERS.map((value) => (
              <option key={value} value={value}>
                {FOLDER_LABELS[value]}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field id="reclassify-in-domain" label={RECLASSIFY_COPY.inDomain}>
        {({ id }) => (
          <input id={id} type="checkbox" checked={inDomain} onChange={(event) => setInDomain(event.target.checked)} />
        )}
      </Field>
      <p className="reclassify-note">{RECLASSIFY_COPY.correctionNote}</p>
      {isApiError(mutation.error) ? (
        <p className="tone-danger" role="alert">
          {mutation.error.message}
        </p>
      ) : null}
    </Dialog>
  )
}
